import {
    DEFAULT_CBC_IMAGE,
    DEFAULT_LEAD_IMAGE,
    DEFAULT_LEAD_VIDEO_OR_AUDIO,
    DEFAULT_LEAD_SEGMENT_OR_EPISODE_AUDIO,
    DEFAULT_LEAD_PHOTOGALLERY,
} from "../../../actions/Detail/constants";

/****************
 **** IMAGES ****
 ****************/
/**
 * Get Author Bio Image from transformed content item
 *
 * @param {Object} json Transformed contentItem Object as returned by transformGQLResponseToDetailStructure
 * @returns {string} Author Bio Image URL
 */
export const getAuthorBioImage = (json) => {
    return (
        (json &&
            json.type === "authorbio" &&
            json.photo?.derivatives?.square_140?.fileurl) ||
        ""
    );
};

/**
 * Get headline or leadMedia Image from transformed content item
 *
 * @param {Object} json Transformed contentItem Object as returned by transformGQLResponseToDetailStructure, or author Object as returned by fetchAuthor
 * @returns {string} Headline Image URL
 */
export const createMetadataImage = (json) => {
    if (json?.image?.aspects?.ratio16x9?.url) {
        return `${json.image.aspects.ratio16x9.url}Resize%3D620`;
    }
    return DEFAULT_CBC_IMAGE;
};

/**
 * Extracts data from transformed content item for image lead media on detail pages
 *
 * @param {Object} json Transformed contentItem Object returned by transformGQLResponseToDetailStructure
 * @param {Object} json.leadMedia
 * @returns {Object | null} Object containing data used for image lead media
 */
export const createLeadImage = (json) => {
    if (!json?.leadMedia || json.leadMedia?.__typename !== "Image") {
        return null;
    }
    const imageLead = json.leadMedia;

    let imageURL = "";
    if (imageLead?.useOriginalImage) {
        imageURL = imageLead.url;
    } else if (imageLead?.aspects?.ratio16x9?.url) {
        imageURL = `${imageLead.aspects.ratio16x9.url}Resize%3D780`;
    }

    if (!imageURL) {
        return null;
    }

    return {
        title: imageLead?.title || DEFAULT_LEAD_IMAGE.title,
        credit: imageLead?.credit || DEFAULT_LEAD_IMAGE.credit,
        url: imageURL || DEFAULT_LEAD_IMAGE.url,
        showcaption: !!(imageLead?.description || imageLead?.credit),
        description: imageLead?.description || DEFAULT_LEAD_IMAGE.description,
        type: "leadmedia-story",
        mass: "full",
        alt: imageLead?.altText || DEFAULT_LEAD_IMAGE.alt,
        isAboveFold: DEFAULT_LEAD_IMAGE.isAboveFold,
    };
};

/*****************
 ***** MEDIA *****
 *****************/
/**
 * Extracts data from transformed content item for video or audio lead media on detail pages (does not include segment or episode audio)
 *
 * @param {Object} json Transformed contentItem Object returned by transformGQLResponseToDetailStructure
 * @param {Object} json.leadMedia
 * @param {string} json.leadMedia.type
 * @param {string} json.leadMedia.sourceId
 * @param {string} json.leadMedia.description
 * @param {string} json.leadMedia.title
 * @param {string} json.leadMedia.image
 * @param {string} json.leadMedia.categories
 * @param {Object} json.leadMedia.media
 * @param {string} json.leadMedia.showName
 * @returns {Object | null} Object containing data used for video or audio lead media
 */
export const createLeadVideoOrAudio = (json) => {
    if (!json?.leadMedia) {
        return null;
    }

    const leadMedia = json.leadMedia;

    if (
        leadMedia?.type !== "video" &&
        leadMedia?.type !== "audio" &&
        leadMedia?.type !== "media"
    ) {
        return null;
    }

    if (!leadMedia.sourceId || isNaN(leadMedia.media?.duration)) {
        return null;
    }

    const value = {
        ...DEFAULT_LEAD_VIDEO_OR_AUDIO,
        clipID: leadMedia.sourceId,
        runtime: Number(leadMedia.media.duration),
        description: leadMedia.description,
        title: leadMedia.title,
        thumbnail: leadMedia?.image?.aspects?.ratio16x9?.url
            ? `${leadMedia.image.aspects.ratio16x9.url}Resize%3D620`
            : "",
        airDate: Number(leadMedia.publishedAt) || 0,
        captions: { src: leadMedia.media?.hasCaptions },
        categories: leadMedia.categories,
        mediatype: leadMedia.type,
        liveondemand: leadMedia.media?.streamType,
        showName: leadMedia.showName,
        source: leadMedia.source,
    };

    return value;
};

/**
 * Extracts data from transformed content item for segment or episode audio lead media on detail pages
 *
 * @param {Object} json TransformedContentItem Object returned by transformGQLResponseToDetailStructure
 * @param {Object} json.episodemedia
 * @param {Object} json.segmentmedia
 * @returns {Object | null} Object containing data used for segment or episode lead media
 */
export const createLeadSegmentOrEpisodeAudio = (json) => {
    if (!json) {
        return null;
    }

    let audio = null;
    if (json.episodemedia) {
        audio = json.episodemedia;
    } else if (json.segmentmedia) {
        audio = json.segmentmedia;
    }

    if (!audio || (!audio.sourceId && isNaN(audio.media?.duration))) {
        return null;
    }

    return {
        ...DEFAULT_LEAD_SEGMENT_OR_EPISODE_AUDIO,
        runtime: Number(audio.media?.duration),
        clipID: audio.sourceId || "",
        description: audio.description || "",
        title: audio.title,
        thumbnail: audio.image?.aspects?.ratio16x9?.url
            ? `${audio.image.aspects.ratio16x9.url}Resize%3D620`
            : "",
        airDate: Number(audio.publishedAt) || 0,
        captions: { src: audio.media?.hasCaptions },
        showName: audio.showName,
        mediatype: audio.type,
        liveondemand: audio.media?.streamType,
        source: audio.source,
        autoPlay: true,
    };
};

/*****************
 **** GALLERY ****
 *****************/
/**
 * Extracts data from transformed content item for photogallery lead media on detail pages or standalone gallery page
 *
 * @param {Object} json transformedContentItem Object returned by transformGQLResponseToDetailStructure
 * @param {Object} json.type
 * @param {Object} json.leadMedia
 * @param {string} json.leadMedia.type
 * @param {string} json.leadMedia.sourceId
 * @returns {Object | null} Object containing data used for photogallery lead media
 */
export const createLeadPhotogallery = (json) => {
    if (!json) {
        return null;
    }

    if (
        json.type !== "photogallery" &&
        json.leadMedia?.type !== "photogallery"
    ) {
        return null;
    }

    const id =
        (json.type === "photogallery" && json.id) ||
        (json.leadMedia?.type === "photogallery" && json.leadMedia?.sourceId) ||
        "";
    const aspectRatio =
        json.type === "photogallery"
            ? json?.photoGallery?.aspectRatio
            : json.leadMedia?.photoGallery?.aspectRatio;

    return {
        ...DEFAULT_LEAD_PHOTOGALLERY,
        id,
        aspectRatio,
    };
};
